import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDocs, query, where, collection } from 'firebase/firestore';
import { auth, db } from '../firebase';
import IconLogo from '../assets/Icon_logo.svg';
import { providerService } from '../services/providerService';
import { useLanguage } from '../contexts/LanguageContext';

const ProviderRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    businessName: '',
    specialty: '',
    description: '',
    location: '',
    referralCode: '',
    agreeToTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [referrerInfo, setReferrerInfo] = useState(null);

  const specialties = [
    'plumbing', 'electrical', 'hvac', 'carpentry', 'painting', 
    'cleaning', 'landscaping', 'roofing', 'general'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNext = () => {
    // Basic validation for step 1
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    setError('');
    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
    setError('');
  };

  // Check for referral code on component mount
  useEffect(() => {
    const referralCode = searchParams.get('ref');
    if (referralCode) {
      // Auto-fill the referral code field
      setFormData(prev => ({
        ...prev,
        referralCode: referralCode
      }));
      findReferrer(referralCode);
    }
  }, [searchParams]);

  const findReferrer = async (referralCode) => {
    try {
      const q = query(
        collection(db, 'users'),
        where('referralCode', '==', referralCode)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const referrer = querySnapshot.docs[0].data();
        setReferrerInfo({
          id: querySnapshot.docs[0].id,
          name: referrer.displayName || `${referrer.firstName} ${referrer.lastName}`,
          referralCode
        });
      }
    } catch (error) {
      console.error('Error finding referrer:', error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;

      // Update profile
      await updateProfile(user, {
        displayName: `${formData.firstName} ${formData.lastName}`
      });

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
        businessName: formData.businessName,
        specialty: formData.specialty,
        description: formData.description,
        location: formData.location,
        role: 'serviceProvider',
        accountType: 'basic', // Will be upgraded to 'premium' after $99 payment
        isActive: false, // Will be activated after admin approval
        isVerified: false, // Will be verified by admin
        isApproved: false, // Admin approval status
        referredBy: referrerInfo ? referrerInfo.id : null,
        referralCode: referrerInfo ? referrerInfo.referralCode : null,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // If referred by someone, create a referral record
      if (referrerInfo) {
        await providerService.createReferralRecord(referrerInfo.id, {
          providerId: user.uid,
          providerName: `${formData.firstName} ${formData.lastName}`,
          providerEmail: formData.email,
          specialty: formData.specialty,
          status: 'pending', // Will be updated after approval
          joinedAt: new Date(),
          rewardAmount: 50
        });
      }

      // User is now automatically signed in after createUserWithEmailAndPassword
      // Redirect to dashboard immediately
      console.log('Provider account created successfully, redirecting to dashboard...');
      navigate('/dashboard');
      setLoading(false);
    } catch (error) {
      console.error('Registration error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else {
        setError('Failed to create account. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <img className="h-12 w-auto" src={IconLogo} alt="EasyProperty" />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                {t('registration.serviceProviderRegistration.navigation.home')}
              </Link>
              <Link to="/login" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                {t('registration.serviceProviderRegistration.navigation.signIn')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('registration.serviceProviderRegistration.title')}
          </h1>
          <p className="text-gray-600">
            {t('registration.serviceProviderRegistration.subtitle')}
          </p>
          {referrerInfo && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span className="text-sm text-green-700">
                  {t('registration.serviceProviderRegistration.referral.invitedBy')} <span className="font-semibold">{referrerInfo.name}</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {t('registration.serviceProviderRegistration.steps.step1')}
            </div>
            <div className={`w-16 h-1 mx-2 ${
              currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-200'
            }`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {t('registration.serviceProviderRegistration.steps.step2')}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('registration.serviceProviderRegistration.steps.accountInfo')}</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('registration.serviceProviderRegistration.form.firstName')} *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('registration.serviceProviderRegistration.form.lastName')} *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.emailAddress')} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.password')} *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.confirmPassword')} *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.referralCode')} {referrerInfo ? `(${t('registration.serviceProviderRegistration.form.invited')})` : `(${t('registration.serviceProviderRegistration.form.optional')})`}
                </label>
                <input
                  type="text"
                  id="referralCode"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  disabled={!!referrerInfo}
                  placeholder={referrerInfo ? t('registration.serviceProviderRegistration.placeholders.invitedPlaceholder') : t('registration.serviceProviderRegistration.placeholders.referralCodePlaceholder')}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    referrerInfo ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                  }`}
                />
                {referrerInfo && (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ {t('registration.serviceProviderRegistration.referral.invitedByReferrer', { name: referrerInfo.name })}
                  </p>
                )}
              </div>

              <button
                onClick={handleNext}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                {t('registration.serviceProviderRegistration.buttons.continueToBusiness')}
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('registration.serviceProviderRegistration.steps.businessInfo')}</h2>
              
              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.businessName')} *
                </label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.phoneNumber')} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="specialty" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.specialty')} *
                </label>
                <select
                  id="specialty"
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('registration.serviceProviderRegistration.placeholders.selectSpecialty')}</option>
                  {specialties.map(specialty => (
                    <option key={specialty} value={specialty}>
                      {t(`registration.serviceProviderRegistration.specialties.${specialty}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.serviceArea')} *
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder={t('registration.serviceProviderRegistration.placeholders.serviceAreaPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('registration.serviceProviderRegistration.form.businessDescription')}
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder={t('registration.serviceProviderRegistration.placeholders.businessDescriptionPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-700">
                  {t('registration.serviceProviderRegistration.terms.agreeTo')}{' '}
                  <button type="button" className="text-blue-600 hover:text-blue-700">{t('registration.serviceProviderRegistration.terms.termsOfService')}</button>
                  {' '}{t('registration.serviceProviderRegistration.terms.and')}{' '}
                  <button type="button" className="text-blue-600 hover:text-blue-700">{t('registration.serviceProviderRegistration.terms.privacyPolicy')}</button>
                </label>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleBack}
                  className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                >
                  {t('registration.serviceProviderRegistration.buttons.back')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !formData.agreeToTerms}
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('registration.serviceProviderRegistration.buttons.creatingAccount') : t('registration.serviceProviderRegistration.buttons.completeRegistration')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Registration Info */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-blue-800">{t('registration.serviceProviderRegistration.info.freeRegistration')}</h3>
          </div>
          <ul className="text-sm text-blue-700 space-y-2">
            {t('registration.serviceProviderRegistration.info.benefits').map((benefit, index) => (
              <li key={index}>• {benefit}</li>
            ))}
          </ul>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {t('registration.serviceProviderRegistration.footer.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              {t('registration.serviceProviderRegistration.footer.signInHere')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProviderRegistration; 